import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Page } from "../../../common/components/Page";
import { clubCoachRatingApi } from "../ratingApi";
import { programApi } from "@bw/shared/api";

export function RateCoachPage() {
  const { programId = "" } = useParams();
  const program = useQuery({
    queryKey: ["classes", programId, "rating-program"],
    queryFn: () => programApi.getProgram(programId),
    enabled: Boolean(programId),
  });
  const coaches = useQuery({
    queryKey: ["classes", programId, "rateable-coaches"],
    queryFn: () => clubCoachRatingApi.rateable(programId),
    enabled: Boolean(programId),
  });
  const [value, setValue] = useState<number | null>(null);
  const [selectedCoach, setSelectedCoach] = useState("");
  const coachProfileId =
    selectedCoach ||
    (coaches.data?.length === 1 ? coaches.data[0].coach_profile_id : "");
  const submit = useMutation({
    mutationFn: () =>
      clubCoachRatingApi.create(coachProfileId, programId, value ?? 0),
  });
  if (program.isLoading || coaches.isLoading)
    return (
      <Page title="Rate your coach">
        <Stack direction="row" spacing={1} role="status">
          <CircularProgress size={20} />
          <Typography>Loading class…</Typography>
        </Stack>
      </Page>
    );
  if (
    program.isError ||
    !program.data ||
    coaches.isError ||
    !coaches.data?.length
  )
    return (
      <Page title="Rate your coach">
        <Alert severity="warning">
          No completed coaching record is available to rate for this class.
        </Alert>
      </Page>
    );
  return (
    <Page title="Rate your coach">
      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        <Typography>
          Rate the coaching you received in {program.data.name}. Ratings are
          attached only to this club&apos;s coach profile.
        </Typography>
        {coaches.data.length > 1 ? (
          <FormControl fullWidth>
            <InputLabel id="rate-coach-label">Coach</InputLabel>
            <Select
              labelId="rate-coach-label"
              label="Coach"
              value={selectedCoach}
              onChange={(event) => setSelectedCoach(event.target.value)}
            >
              {coaches.data.map((coach) => (
                <MenuItem
                  key={coach.coach_profile_id}
                  value={coach.coach_profile_id}
                >
                  {coach.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography sx={{ fontWeight: 700 }}>
            {coaches.data[0].display_name}
          </Typography>
        )}
        <Rating
          aria-label="Coach rating"
          size="large"
          value={value}
          onChange={(_, next) => setValue(next)}
        />
        <Button
          disabled={
            value == null ||
            !coachProfileId ||
            submit.isPending ||
            submit.isSuccess
          }
          onClick={() => submit.mutate()}
          sx={{ alignSelf: "flex-start" }}
          variant="contained"
        >
          Submit rating
        </Button>
        {submit.isSuccess ? (
          <Alert
            severity="success"
            action={
              <Button component={RouterLink} to={`/coaches/${coachProfileId}`}>
                View coach
              </Button>
            }
          >
            Rating submitted.
          </Alert>
        ) : null}
        {submit.isError ? (
          <Alert severity="error">
            This rating is not available. The class must be complete, and you
            must have qualifying attendance with this coach.
          </Alert>
        ) : null}
      </Stack>
    </Page>
  );
}
